import { Anchor, Box, Button, LoadingOverlay, Table, Text, Title } from "@mantine/core"
import { modals } from "@mantine/modals"
import { Page } from "../../components/layout/Page";
import { Breadcrumb } from "../../components/layout/breadcrumb";
import {
  ArrowClockwiseIcon,
  CheckIcon,
  FileTextIcon,
  ScissorsIcon,
  SealCheckIcon,
} from "@phosphor-icons/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { getModuleUrl } from "@dehub/theme"
import useSwr from "swr"
import { EbarimtApi, InvoiceApi } from "../../apis"
import { httpRequest } from "../../apis/invoice"
import { AmendmentBanner } from "../../components/invoice-amendment/AmendmentBanner"
import { AmendmentHistory } from "../../components/invoice-amendment/AmendmentHistory"
import {
  buildProposalDisplayLines,
  computeAmendmentLineChanges,
  lineChangeKeyForInvoiceLine,
} from "../../components/invoice-amendment/amendment-line-diff"
import { handleAmendError } from "../../components/invoice-amendment/amend-error"
import { SafeRelatedReturns } from "../../components/invoice-detail/SafeRelatedReturns"
import { usePermission } from "../../components/permission"
import { usePdfDownload } from "../../hooks/usePdfDownload"
import { useRowLoading } from "../../hooks/useRowLoading"
import { IAuth } from "../../interfaces/IAuth"
import { currencyFormat } from "../../utils"
import { dateFormat, dateTimeFormat } from "../../utils/date"
import { handleError } from "../../utils/error-handler"
import { message } from "../../utils/Message"
import modalChrome from "../../components/ui/app-modal.module.css";
import grid from "../../components/ui/data-grid.module.css";
import { renderBusinessName } from "../../utils/render-business-name"
import { renderName } from "../../utils/render-name"
import classes from "../sales-invoice/details.module.css"
import { ReasonTooltip } from "../../components/ui/reason-tooltip";

type ClosedStepKey = "draft" | "sent" | "confirmed" | "paid" | "closed"

function collectionDaysBetween(from?: string | null, to?: string | null): number | null {
  if (!from || !to) return null
  const a = new Date(from)
  const b = new Date(to)
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null
  const startA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())
  const startB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate())
  return Math.max(0, Math.round((startB - startA) / 86_400_000))
}

function fileExt(nameOrUrl?: string | null): string {
  if (!nameOrUrl) return "FILE"
  const clean = nameOrUrl.split("?")[0] ?? nameOrUrl
  const base = clean.split("/").pop() ?? clean
  const parts = base.split(".")
  if (parts.length < 2) return "FILE"
  return (parts.pop() ?? "FILE").slice(0, 4).toUpperCase()
}

export const ClosedInvoiceDetails = () => {
  const params = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation(["invoice", "common"])
  const listSearch = location.search || ""

  const INV_LIST_CLOSED = usePermission("INV_LIST_CLOSED")
  const INV_LIST_CLOSED_PDF = usePermission("INV_LIST_CLOSED_PDF")
  const INV_LIST_CLOSED_EBARIMT = usePermission("INV_LIST_CLOSED_EBARIMT")
  const INV_AMEND = usePermission("INV_AMEND")

  const myBusinessType = useSelector((state: { auth: IAuth }) => state.auth.myBusinessType)

  const { download: downloadPdf, isLoading: isPdfLoading } = usePdfDownload({ fetcher: InvoiceApi.pdf })
  const { addLoad: addEload, removeLoad: removeEload, isLoading: isEloading } = useRowLoading()
  const { addLoad: addEReturn, removeLoad: removeEReturn, isLoading: isEReturning } = useRowLoading()
  const [pdfOpen, setPdfOpen] = useState(false)
  const [pdfUrl, setPdfUrl] = useState("")
  const [withdrawing, setWithdrawing] = useState(false)
  const [lineView, setLineView] = useState<"current" | "proposal">("current")

  const { data, isLoading, mutate } = useSwr<any>(
    params.id !== "new" && INV_LIST_CLOSED.hasAccess ? `/api/invoice/${params.id}` : null,
    params.id !== "new" && INV_LIST_CLOSED.hasAccess ? async () => await InvoiceApi.get(`${params.id}`) : null,
  )

  const { data: amendability, mutate: mutateAmendability } = useSwr(
    params.id && INV_LIST_CLOSED.hasAccess ? `/api/invoice/${params.id}/amendability` : null,
    () => InvoiceApi.amendability(`${params.id}`),
  )

  if (isLoading) return <Box pos="relative" mih={320}><LoadingOverlay visible /></Box>
  if (!INV_LIST_CLOSED.hasAccess) return <Page>{INV_LIST_CLOSED.accessDenied()}</Page>

  const d: any = data ?? {}
  const stepOrder: ClosedStepKey[] = ["draft", "sent", "confirmed", "paid", "closed"]

  const lines: any[] = Array.isArray(d.lines) ? d.lines : []
  const additionalLines: any[] = Array.isArray(d.additionalLines) ? d.additionalLines : []
  const attachmentsRaw: any[] = Array.isArray(d.attachments) ? d.attachments : []
  const attachments = attachmentsRaw.filter((a) => Boolean(a?.url || a?.name))
  const descriptionText = d.description?.trim() ?? ""
  const hasAttachmentsCard = Boolean(descriptionText) || attachments.length > 0
  const pending = amendability?.enabled ? amendability.pending : undefined
  const proposalCode = pending?.refCode?.includes("-")
    ? pending.refCode.split("-").slice(-1)[0]
    : "A1"

  const itemsTotal = Number(d.itemsTotal ?? 0)
  const vatAmount = Number(d.vatAmount ?? 0)
  const taxAmount = Number(d.taxAmount ?? 0)
  const shippingAmount = Number(d.shippingAmount ?? 0)
  const discountAmount = Number(d.discountAmount ?? 0)
  const grandTotal = Number(d.totalAmount ?? d.confirmedAmount ?? 0)
  const additionalAmount = Array.isArray(d.additionalLines)
    ? d.additionalLines.reduce((s: number, it: any) => s + Number(it?.quantity ?? 0) * Number(it?.price ?? 0), 0)
    : 0
  const addOns = taxAmount + additionalAmount + shippingAmount
  const exclusiveGuess = itemsTotal + vatAmount + addOns - discountAmount
  const inclusiveGuess = itemsTotal + addOns - discountAmount
  const vatIncluded = vatAmount > 0 && Math.abs(inclusiveGuess - grandTotal) < Math.abs(exclusiveGuess - grandTotal)
  const runningBeforeDiscount = itemsTotal + addOns + (vatIncluded ? 0 : vatAmount)
  const reconcileDiscount = Math.max(0, Math.round((runningBeforeDiscount - grandTotal) * 100) / 100)
  const discountPct = itemsTotal > 0 ? Math.round((reconcileDiscount / itemsTotal) * 1000) / 10 : 0
  const paid = Number(d.paidAmount ?? 0)
  const lineChanges = pending
    ? computeAmendmentLineChanges(pending, lines, additionalLines)
    : []
  const changeByKey = new Map(lineChanges.map((c) => [c.key, c]))
  const showProposal = Boolean(pending && lineView === "proposal")
  const displayLines = showProposal
    ? buildProposalDisplayLines(pending!, lines, additionalLines)
    : lines.map((li) => ({ ...li, _changeKey: lineChangeKeyForInvoiceLine(li, false), _was: null, _removed: false }))
  const proposalGrand = pending ? Number(pending.newTotal) : grandTotal
  const proposalPrevious = pending ? Number(pending.previousTotal) : grandTotal
  const proposalVat =
    showProposal && pending?.newVat != null && Number(pending.newVat) > 0
      ? Number(pending.newVat)
      : null
  const proposalVatExclusive = proposalVat != null && pending?.vatMode === "EXCLUSIVE"
  const proposalVatInclusive = proposalVat != null && pending?.vatMode === "INCLUSIVE"
  const remainingUnpaid = Math.max(0, Math.round((grandTotal - paid) * 100) / 100)
  const paidPct = grandTotal > 0 ? Math.min(100, Math.max(0, (paid / grandTotal) * 100)) : 0
  const paidPctRounded = Math.round(paidPct)
  const fullyPaid = paid >= grandTotal && grandTotal > 0
  const collectionDays = collectionDaysBetween(d.confirmedDate, d.paidDate)

  const sender = d.senderBusiness ?? d.senderBusinessProfile ?? null
  const receiver = d.receiverBusiness ?? null
  const contactEmail = receiver?.email ?? sender?.email ?? null
  const contactPhone = receiver?.phone ?? sender?.phone ?? null
  const contactAddress = receiver?.address ?? sender?.address ?? null

  const poCode = d.salesOrderCode ?? d.orderSalesCode ?? d.purchaseSalesCode ?? d.orderPurchaseCode ?? null
  const confirmedDateLabel = d.confirmedDate ? dateFormat(d.confirmedDate) : null
  const closedDateLabel = d.paidDate ? dateFormat(d.paidDate) : null
  const reconciled = !!d.ebarimtRegistered
  const ebarimtAt = d.ebarimtRegisteredAt ?? d.ebarimtDate ?? d.ebarimtCreatedAt ?? null

  const receiverLabel = (() => {
    const name = renderBusinessName(receiver, "-")
    const ttd = receiver?.regNumber ?? receiver?.partner?.regNumber
    return ttd ? `${name} · ${t("invoice:detail.meta.ttd")} ${ttd}` : name
  })()

  const handleEbarimt = async () => {
    if (!d.id) return
    addEload(d.id)
    if (reconciled) {
      setPdfOpen(true)
      setPdfUrl(d.barimtUrl)
      removeEload(d.id)
    } else {
      try {
        const res = await EbarimtApi.ebarimt(d.id)
        if (res && res.status === "SUCCESS") {
          setPdfOpen(true)
          setPdfUrl(res.url)
          await mutate()
        }
      } catch (error) {
        handleError(error)
      } finally {
        removeEload(d.id)
      }
    }
  }

  const handleWithdrawAmendment = async () => {
    if (!params.id || !amendability?.pending?.id) return
    try {
      setWithdrawing(true)
      await InvoiceApi.withdrawAmendment(`${params.id}`, amendability.pending.id)
      message.success(t("invoice:amend.withdrawnToast"))
      await Promise.all([mutate(), mutateAmendability()])
    } catch (error) {
      handleAmendError(error)
    } finally {
      setWithdrawing(false)
    }
  }

  const handleEbarimtReturn = () => {
    if (!d.id) return
    addEReturn(d.id)
    modals.openConfirmModal({
      title: t("invoice:ebarimt.returnTitle"),
      children: <Text size="sm" ta="center">{t("invoice:ebarimt.returnText", { code: d.refCode })}</Text>,
      labels: { confirm: t("invoice:ebarimt.returnConfirm"), cancel: t("invoice:ebarimt.returnCancel") },
      confirmProps: { color: "red" },
      cancelProps: { variant: "default" },
      classNames: {
        title: modalChrome.confirmTitle,
        body: modalChrome.confirmModalBody,
        header: modalChrome.modalHeader,
      },
      onClose: () => removeEReturn(d.id),
      onCancel: () => removeEReturn(d.id),
      onConfirm: async () => {
        try {
          const res = await EbarimtApi.ebarimtReturn(d.id)
          if (res) {
            message.success(t("invoice:ebarimt.returnSuccess"))
            await mutate()
          }
        } catch (error) {
          handleError(error)
        } finally {
          removeEReturn(d.id)
        }
      },
    })
  }

  return (
    <Page>
      <Box className={classes.pageHdr}>
        <Box className={classes.pageHdrLeft}>
          <Breadcrumb data={[
              { label: t("invoice:breadcrumb.salesInvoice") },
              { label: t("invoice:detail.crumbClosed"), to: `/closed-invoice${listSearch}` },
              { label: d.refCode ?? "-" },
            ]} />

          <Box className={classes.titleRow}>
            <Title order={1} className={classes.pageTitle}>{d.refCode ?? "-"}</Title>
            <Box component="span" className={`${classes.statusPill} ${classes.statusPillClosed}`}>
              <Box component="span" className={classes.statusPillDot} />
              {t("invoice:detail.closed")}
            </Box>
            <Box
              component="span"
              className={`${classes.statusPill} ${
                reconciled ? classes.statusPillEbarimtOk : classes.statusPillEbarimtMissing
              }`}
            >
              <Box component="span" className={classes.statusPillDot} />
              {reconciled ? t("invoice:detail.ebarimtRegistered") : t("invoice:detail.ebarimtMissing")}
            </Box>
            {pending && (
              <Box component="span" className={`${classes.statusPill} ${classes.statusPillAmendPending}`}>
                <Box component="span" className={classes.statusPillDot} />
                {t("invoice:amend.pendingStatusPill")}
              </Box>
            )}
          </Box>

          <Text className={classes.pageSub}>
            {renderBusinessName(receiver, "-")}
            {confirmedDateLabel && <> · {t("invoice:detail.subtitleConfirmed", { date: confirmedDateLabel })}</>}
            {closedDateLabel && <> · {t("invoice:detail.subtitleClosed", { date: closedDateLabel })}</>}
            {collectionDays != null && <> · {t("invoice:detail.subtitleCollection", { days: collectionDays })}</>}
          </Text>
        </Box>

        <Box className={classes.pageHdrRight}>
            <ReasonTooltip reason={INV_LIST_CLOSED_PDF.reason}>
              <Button
                variant="default"
                classNames={{ root: classes.actionBtn }}
                leftSection={<FileTextIcon size={14} />}
                loading={isPdfLoading(d.id)}
                onClick={() => d.id && downloadPdf(`${d.id}`)}
              >
                {t("invoice:detail.actions.pdf")}
              </Button>
            </ReasonTooltip>
          {myBusinessType === "SUPPLIER" && d.invoiceStatus === "CLOSED" && (
            <ReasonTooltip reason={INV_LIST_CLOSED_EBARIMT.reason}>
              <Button
                variant={reconciled ? "default" : undefined}
                color={reconciled ? undefined : "brand"}
                classNames={{ root: reconciled ? classes.actionBtn : classes.actionBtnPrimary }}
                leftSection={<SealCheckIcon size={14} weight={reconciled ? "fill" : "regular"} />}
                loading={isEloading(d.id)}
                onClick={handleEbarimt}
              >
                {reconciled ? t("invoice:detail.ebarimtView") : t("invoice:detail.ebarimtCreate")}
              </Button>
            </ReasonTooltip>
          )}
          {myBusinessType === "SUPPLIER" && reconciled && d.invoiceStatus === "CLOSED" && (
            <ReasonTooltip reason={INV_LIST_CLOSED_EBARIMT.reason}>
              <Button
                variant="subtle"
                color="red"
                leftSection={<ArrowClockwiseIcon size={14} />}
                loading={isEReturning(d.id)}
                onClick={handleEbarimtReturn}
              >
                {t("invoice:detail.ebarimtReturn")}
              </Button>
            </ReasonTooltip>
          )}
          {myBusinessType === "SUPPLIER" && amendability?.enabled && amendability.amendable && (
            <ReasonTooltip reason={INV_AMEND.reason}>
              <Button
                variant="default"
                classNames={{ root: classes.actionBtn }}
                leftSection={<ScissorsIcon size={14} />}
                onClick={() => navigate(`/sales-invoice/${params.id}/amend`)}
              >
                {t("invoice:amend.button")}
              </Button>
            </ReasonTooltip>
          )}
        </Box>
      </Box>

      {amendability?.enabled && amendability.pending && (
        <AmendmentBanner
          pending={amendability.pending}
          mode="seller"
          showWithdraw={myBusinessType === "SUPPLIER" && INV_AMEND.hasAccess}
          withdrawing={withdrawing}
          onWithdraw={handleWithdrawAmendment}
          paidAmount={paid}
          invoiceStatus={d.invoiceStatus}
          currentLines={lines}
          currentAdditionalLines={additionalLines}
          onShowProposal={() => setLineView("proposal")}
        />
      )}

      <Box className={classes.grid}>
        <Box className={classes.colMain}>
          <Box className={`${classes.kpiCard} ${classes.orderKpi}`}>
            <Box className={classes.kpiGrid}>
              <Box>
                <Text className={classes.kpiLabel}>{t("invoice:detail.kpi.total")}</Text>
                <Text className={classes.kpiValue}>{currencyFormat(grandTotal)}</Text>
              </Box>
              <Box>
                <Text className={classes.kpiLabel}>{t("invoice:detail.kpi.paid")}</Text>
                <Text className={classes.kpiValuePaid}>{currencyFormat(paid)}</Text>
              </Box>
              <Box>
                <Text className={classes.kpiLabel}>{t("invoice:detail.kpi.collectionDays")}</Text>
                <Text className={classes.kpiValue}>
                  {collectionDays != null
                    ? t("invoice:detail.kpi.collectionDaysValue", { days: collectionDays })
                    : "-"}
                </Text>
              </Box>
            </Box>
            <Box>
              <Box className={classes.kpiBar}>
                <Box
                  className={classes.kpiBarFill}
                  style={{
                    width: `${fullyPaid ? 100 : paidPct}%`,
                    background: fullyPaid
                      ? "var(--mantine-color-green-8)"
                      : "var(--mantine-color-yellow-6)",
                  }}
                />
              </Box>
              <Text className={classes.kpiMeta} mt={6}>
                {closedDateLabel
                  ? t("invoice:detail.kpi.closedFully", { pct: fullyPaid ? 100 : paidPctRounded, date: closedDateLabel })
                  : t("invoice:detail.kpi.paidPctOnly", { pct: paidPctRounded })}
              </Text>
              {!fullyPaid && remainingUnpaid > 0 && (
                <Text className={classes.kpiWarn}>
                  {t("invoice:detail.kpi.unpaidClosed", { amount: currencyFormat(remainingUnpaid) })}
                </Text>
              )}
            </Box>
          </Box>

          <Box className={`${classes.card} ${classes.orderItems}`}>
            <Box className={classes.cardHead}>
              <Title order={2} className={classes.cardTitle}>{t("invoice:detail.itemsHead")}</Title>
              {pending ? (
                <Box className={classes.lineViewSeg} role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={lineView === "current"}
                    className={`${classes.lineViewTab} ${lineView === "current" ? classes.lineViewTabActive : ""}`}
                    onClick={() => setLineView("current")}
                  >
                    {t("invoice:amend.linesViewCurrent")}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={lineView === "proposal"}
                    className={`${classes.lineViewTab} ${lineView === "proposal" ? classes.lineViewTabActive : ""}`}
                    onClick={() => setLineView("proposal")}
                  >
                    {t("invoice:amend.linesViewProposal", { code: proposalCode })}
                  </button>
                </Box>
              ) : (
                <Text className={classes.cardSub}>{t("invoice:detail.lineRows", { count: lines.length })}</Text>
              )}
            </Box>
            {displayLines.length === 0 ? (
              <Box className={classes.emptyInline}>{t("common:noData")}</Box>
            ) : (
              <Box className={classes.lineTable}>
                <Table className={grid.table} horizontalSpacing={0} verticalSpacing={0} withRowBorders={false}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t("invoice:detail.lineCol.product")}</Table.Th>
                      <Table.Th className={classes.num}>{t("invoice:detail.lineCol.qty")}</Table.Th>
                      <Table.Th className={classes.num}>{t("invoice:detail.lineCol.unitPrice")}</Table.Th>
                      <Table.Th className={classes.num}>{t("invoice:detail.lineCol.discount")}</Table.Th>
                      <Table.Th className={classes.num}>{t("invoice:detail.lineCol.amount")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {displayLines.map((li: any, i: number) => {
                      const qty = Number(li.quantity ?? li.qty ?? 0)
                      const price = Number(li.price ?? li.unitPrice ?? 0)
                      const disc = Number(li.discountAmount ?? li.discount ?? 0)
                      const lineTot = Number(li.totalAmount ?? li.total ?? qty * price - disc)
                      const code = li.skuCode ?? li.barcode ?? li.refCode ?? li.code ?? li.sku ?? null
                      const unit = typeof li.unit === "string" ? li.unit : (li.unit?.name ?? li.unitName ?? null)
                      const ch = showProposal ? changeByKey.get(li._changeKey) : undefined
                      const rowCls = li._removed
                        ? classes.lineRowRemoved
                        : ch?.kind === "changed"
                          ? classes.lineRowChanged
                          : ch?.kind === "added"
                            ? classes.lineRowAdded
                            : undefined
                      const was = li._was
                      const qtyBold = Boolean(was?.quantity != null)
                      const priceBold = Boolean(was?.price != null)
                      const totalBold =
                        !showProposal || Boolean(was?.total != null) || ch?.kind === "added"
                      const nameBold = Boolean(ch && !li._removed)
                      return (
                        <Table.Tr key={li.id ?? li.variantId ?? i} className={rowCls}>
                          <Table.Td>
                            <Text
                              className={`${classes.lineName}${li._removed ? ` ${classes.lineWas}` : ""}`}
                              fw={nameBold ? 600 : undefined}
                            >
                              {li.name ?? li.productName ?? li.description ?? "-"}
                            </Text>
                            {(code || unit) && (
                              <Box className={classes.lineSku}>
                                {code && <Text component="span" className={classes.lineSkuCode}>{code}</Text>}
                                {code && unit && (
                                  <Text component="span" className={classes.lineSkuUnit}>{" · "}</Text>
                                )}
                                {unit && <Text component="span" className={classes.lineSkuUnit}>{unit}</Text>}
                              </Box>
                            )}
                          </Table.Td>
                          <Table.Td className={classes.num}>
                            <Text
                              component="span"
                              fw={qtyBold ? 600 : undefined}
                              className={
                                li._removed
                                  ? classes.lineWas
                                  : ch?.kind === "changed" && !qtyBold
                                    ? classes.lineFieldMuted
                                    : undefined
                              }
                            >
                              {qty}
                            </Text>
                            {was?.quantity != null && (
                              <Text component="span" className={classes.lineWas}>{was.quantity}</Text>
                            )}
                          </Table.Td>
                          <Table.Td className={`${classes.num} ${classes.amountNowrap}`}>
                            <Text
                              component="span"
                              fw={priceBold ? 600 : undefined}
                              className={
                                li._removed
                                  ? classes.lineWas
                                  : ch?.kind === "changed" && !priceBold
                                    ? classes.lineFieldMuted
                                    : undefined
                              }
                            >
                              {currencyFormat(price, { withSymbol: false })}
                            </Text>
                            {was?.price != null && (
                              <Text component="span" className={classes.lineWas}>
                                {currencyFormat(was.price, { withSymbol: false })}
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td
                            className={`${classes.num} ${disc > 0 ? classes.lineDiscountActive : classes.lineDiscount}`}
                          >
                            {disc > 0 ? `−${currencyFormat(disc, { withSymbol: false })}` : "-"}
                          </Table.Td>
                          <Table.Td
                            className={`${classes.num} ${classes.amountNowrap} ${
                              totalBold && !li._removed ? classes.lineTotal : ""
                            } ${ch?.kind === "changed" && !totalBold ? classes.lineFieldMuted : ""}`}
                          >
                            <Text
                              component="span"
                              fw={totalBold ? 600 : undefined}
                              className={li._removed ? classes.lineWas : undefined}
                            >
                              {currencyFormat(lineTot, { withSymbol: false })}
                            </Text>
                            {was?.total != null && (
                              <Text component="span" className={classes.lineWas}>
                                {currencyFormat(was.total, { withSymbol: false })}
                              </Text>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      )
                    })}
                  </Table.Tbody>
                  <Table.Tfoot>
                    {!showProposal && taxAmount > 0 && (
                      <Table.Tr className={classes.tfootRow}>
                        <Table.Td colSpan={4} className={classes.num}>{t("invoice:detail.totals.tax")}</Table.Td>
                        <Table.Td className={classes.num}>{currencyFormat(taxAmount, { withSymbol: false })}</Table.Td>
                      </Table.Tr>
                    )}
                    {!showProposal && additionalAmount > 0 && (
                      <Table.Tr className={classes.tfootRow}>
                        <Table.Td colSpan={4} className={classes.num}>{t("invoice:detail.totals.additionalLines")}</Table.Td>
                        <Table.Td className={classes.num}>{currencyFormat(additionalAmount, { withSymbol: false })}</Table.Td>
                      </Table.Tr>
                    )}
                    {!showProposal && shippingAmount > 0 && (
                      <Table.Tr className={classes.tfootRow}>
                        <Table.Td colSpan={4} className={classes.num}>{t("invoice:detail.totals.shipping")}</Table.Td>
                        <Table.Td className={classes.num}>{currencyFormat(shippingAmount, { withSymbol: false })}</Table.Td>
                      </Table.Tr>
                    )}
                    {!showProposal && !vatIncluded && vatAmount > 0 && (
                      <Table.Tr className={classes.tfootRow}>
                        <Table.Td colSpan={4} className={classes.num}>{t("invoice:detail.totals.vat")}</Table.Td>
                        <Table.Td className={`${classes.num} ${classes.amountNowrap}`}>
                          {currencyFormat(vatAmount, { withSymbol: false })}
                        </Table.Td>
                      </Table.Tr>
                    )}
                    {!showProposal && reconcileDiscount > 0 && (
                      <Table.Tr className={`${classes.tfootRow} ${classes.tfootDiscount}`}>
                        <Table.Td colSpan={4} className={classes.num}>
                          {t("invoice:detail.totals.discount")}
                          {discountPct > 0 ? ` (${discountPct}%)` : ""}
                        </Table.Td>
                        <Table.Td className={`${classes.num} ${classes.amountNowrap}`}>
                          −{currencyFormat(reconcileDiscount, { withSymbol: false })}
                        </Table.Td>
                      </Table.Tr>
                    )}
                    {proposalVatExclusive && (
                      <Table.Tr className={classes.tfootRow}>
                        <Table.Td colSpan={4} className={classes.num}>
                          {t("invoice:detail.totals.vat")}
                        </Table.Td>
                        <Table.Td className={`${classes.num} ${classes.amountNowrap}`}>
                          {currencyFormat(proposalVat!, { withSymbol: false })}
                        </Table.Td>
                      </Table.Tr>
                    )}
                    <Table.Tr className={classes.tfootGrand}>
                      <Table.Td colSpan={4} className={classes.num}>
                        {showProposal ? (
                          <>
                            <Text component="span" className={`${classes.tfootStruck} ${classes.amountNowrap}`}>
                              {t("invoice:amend.linesFooterCurrent")} {currencyFormat(proposalPrevious)}
                            </Text>
                            {" · "}
                            {t("invoice:amend.linesFooterProposal")}
                          </>
                        ) : (
                          t("invoice:detail.totals.grandShort")
                        )}
                      </Table.Td>
                      <Table.Td className={`${classes.num} ${classes.tfootGrandVal}`}>
                        {currencyFormat(showProposal ? proposalGrand : grandTotal)}
                      </Table.Td>
                    </Table.Tr>
                  </Table.Tfoot>
                </Table>
                {!showProposal && vatIncluded && vatAmount > 0 && (
                  <Text className={classes.vatBelow}>
                    {t("invoice:detail.totals.vatOfWhich")} {currencyFormat(vatAmount, { withSymbol: false })}
                  </Text>
                )}
                {proposalVatInclusive && (
                  <Text className={classes.vatBelow}>
                    {t("invoice:detail.totals.vatOfWhich")} {currencyFormat(proposalVat!, { withSymbol: false })}
                  </Text>
                )}
              </Box>
            )}
          </Box>

          <Box className={`${classes.card} ${classes.cardPad} ${classes.orderInfo}`}>
            <Box component="dl" className={classes.metaDl}>
              <Box className={classes.metaItem}>
                <Text component="dt" className={classes.metaDt}>{t("invoice:detail.meta.sender")}</Text>
                <Text component="dd" className={classes.metaDd}>{renderBusinessName(sender, "-")}</Text>
              </Box>
              <Box className={classes.metaItem}>
                <Text component="dt" className={classes.metaDt}>{t("invoice:detail.meta.receiver")}</Text>
                <Text component="dd" className={classes.metaDd}>{receiverLabel}</Text>
              </Box>
              <Box className={classes.metaItem}>
                <Text component="dt" className={classes.metaDt}>{t("invoice:detail.meta.currency")}</Text>
                <Text component="dd" className={classes.metaDd}>MNT</Text>
              </Box>
              <Box className={classes.metaItem}>
                <Text component="dt" className={classes.metaDt}>{t("invoice:detail.meta.confirmed")}</Text>
                <Text component="dd" className={classes.metaDd}>{confirmedDateLabel ?? "-"}</Text>
              </Box>
              <Box className={classes.metaItem}>
                <Text component="dt" className={classes.metaDt}>{t("invoice:detail.meta.closedAt")}</Text>
                <Text component="dd" className={classes.metaDd}>{closedDateLabel ?? "-"}</Text>
              </Box>
              <Box className={classes.metaItem}>
                <Text component="dt" className={classes.metaDt}>{t("invoice:detail.meta.po")}</Text>
                <Text component="dd" className={`${classes.metaDd} ${poCode ? classes.metaMono : classes.metaDdMuted}`}>
                  {poCode ?? "-"}
                </Text>
              </Box>
            </Box>
          </Box>

          {hasAttachmentsCard && (
            <Box className={`${classes.card} ${classes.orderAttach}`}>
              <Box className={classes.cardHead}>
                <Title order={2} className={classes.cardTitle}>{t("invoice:detail.attachments")}</Title>
              </Box>
              <Box className={classes.attachBody}>
                {descriptionText && <Text className={classes.attachNote}>{descriptionText}</Text>}
                {attachments.map((a: any, i: number) => {
                  const url: string = a.url ?? ""
                  const label = a.name || url.split("/").pop() || t("common:noData")
                  return (
                    <Box key={a.id ?? i} className={classes.fileCard}>
                      <Box component="span" className={classes.typeBadge}>{fileExt(a.name || url)}</Box>
                      <Box className={classes.fileMeta}>
                        <Text className={classes.fileName} title={label}>{label}</Text>
                        {url ? (
                          <Anchor href={url} target="_blank" rel="noopener noreferrer" className={classes.fileLink}>
                            {t("invoice:detail.attachOpen")}
                          </Anchor>
                        ) : null}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            </Box>
          )}

          {amendability?.enabled && (amendability.history?.length ?? 0) > 0 && (
            <Box className={classes.orderAmendHist}>
              <AmendmentHistory items={amendability.history ?? []} />
            </Box>
          )}
        </Box>

        <Box className={classes.colSide}>
          <Box className={`${classes.card} ${classes.cardPadSide} ${classes.orderProgress}`}>
            <Title order={2} className={classes.cardTitle} mb={2}>{t("invoice:detail.progressTitle")}</Title>
            <Box className={classes.progressList}>
              {stepOrder.map((key) => (
                <Box key={key}>
                  <Box className={classes.progressRow}>
                    <Box className={`${classes.progressDot} ${classes.progressDotDone}`}>
                      <CheckIcon size={11} weight="bold" />
                    </Box>
                    <Box className={classes.progressRowLabel}>
                      <Text component="span" fz={14} fw={400}>
                        {t(`invoice:detail.stepper.${key}`)}
                      </Text>
                      {key === "closed" && closedDateLabel && (
                        <Text className={classes.progressDate}>{closedDateLabel}</Text>
                      )}
                    </Box>
                  </Box>
                  {pending && key === "closed" && (
                    <Box className={`${classes.progressRow} ${classes.progressRowActive}`}>
                      <Box className={`${classes.progressDot} ${classes.progressDotAmend}`} />
                      <Text component="span" fz={14} fw={600}>
                        {t("invoice:amend.progressPending")}
                      </Text>
                    </Box>
                  )}
                </Box>
              ))}
              <Box
                className={`${classes.progressRow} ${reconciled ? "" : classes.progressRowTodo}`}
              >
                <Box
                  className={`${classes.progressDot} ${
                    reconciled ? classes.progressDotDone : classes.progressDotTodo
                  }`}
                >
                  {reconciled ? <CheckIcon size={11} weight="bold" /> : null}
                </Box>
                <Text component="span" fz={14} fw={reconciled ? 400 : 400}>
                  {reconciled ? t("invoice:detail.ebarimtStep") : t("invoice:detail.ebarimtPending")}
                </Text>
              </Box>
            </Box>
          </Box>

          <Box className={`${classes.card} ${classes.cardPadSide} ${classes.orderContact}`}>
            <Title order={2} className={classes.cardTitle} mb={4}>{t("invoice:detail.contactTitle")}</Title>
            <Box component="dl" className={classes.contactDl}>
              <Box className={classes.metaItem}>
                <Text component="dt" className={classes.metaDt}>{t("invoice:detail.meta.receiver")}</Text>
                <Text component="dd" className={classes.metaDd}>{renderBusinessName(receiver, "-")}</Text>
              </Box>
              {contactEmail && (
                <Box className={classes.metaItem}>
                  <Text component="dt" className={classes.metaDt}>{t("invoice:detail.contact.email")}</Text>
                  <Text component="dd" className={classes.metaDd}>{contactEmail}</Text>
                </Box>
              )}
              {contactPhone && (
                <Box className={classes.metaItem}>
                  <Text component="dt" className={classes.metaDt}>{t("invoice:detail.contact.phone")}</Text>
                  <Text component="dd" className={classes.metaDd}>{contactPhone}</Text>
                </Box>
              )}
              {contactAddress && (
                <Box className={classes.metaItem}>
                  <Text component="dt" className={classes.metaDt}>{t("invoice:detail.contact.address")}</Text>
                  <Text component="dd" className={classes.metaDd}>{contactAddress}</Text>
                </Box>
              )}
              {!contactEmail && !contactPhone && !contactAddress && (
                <Text fz={14} c="gray.7">{t("common:noData")}</Text>
              )}
            </Box>
          </Box>

          <Box className={`${classes.historyCard} ${classes.cardPadSide} ${classes.orderHistory}`}>
            <Title order={2} className={classes.cardTitle} mb={4}>{t("invoice:detail.history.title")}</Title>
            <Box className={classes.timeline}>
              {pending && (
                <Box className={classes.tlItem}>
                  <Box className={`${classes.tlDot} ${classes.tlDotAmendPending}`} />
                  <Box>
                    <Text className={classes.tlTitle}>
                      {t("invoice:amend.historyProposed", { ref: proposalCode })}
                    </Text>
                    <Text className={classes.tlSub}>
                      {dateTimeFormat(pending.proposedAt)}
                      {" · "}
                      {t("invoice:amend.historyTotals", {
                        from: currencyFormat(pending.previousTotal, { withSymbol: false }),
                        to: currencyFormat(pending.newTotal, { withSymbol: false }),
                      })}
                    </Text>
                  </Box>
                </Box>
              )}
              {d.paidDate && (
                <Box className={classes.tlItem}>
                  <Box className={`${classes.tlDot} ${classes.tlDotPay}`} />
                  <Box>
                    <Text className={classes.tlTitle}>{t("invoice:detail.history.closed")}</Text>
                    <Text className={classes.tlSub}>{dateTimeFormat(d.paidDate)}</Text>
                  </Box>
                </Box>
              )}
              {reconciled && (
                <Box className={classes.tlItem}>
                  <Box className={`${classes.tlDot} ${classes.tlDotAmend}`} />
                  <Box>
                    <Text className={classes.tlTitle}>{t("invoice:detail.history.ebarimt")}</Text>
                    {ebarimtAt && <Text className={classes.tlSub}>{dateTimeFormat(ebarimtAt)}</Text>}
                  </Box>
                </Box>
              )}
              {d.confirmedDate && (
                <Box className={classes.tlItem}>
                  <Box className={classes.tlDot} />
                  <Box>
                    <Text className={classes.tlTitle}>{t("invoice:detail.history.confirmed")}</Text>
                    <Text className={classes.tlSub}>
                      {[d.confirmedUser ? renderName(d.confirmedUser) : null, dateTimeFormat(d.confirmedDate)]
                        .filter(Boolean)
                        .join(" · ")}
                    </Text>
                  </Box>
                </Box>
              )}
              {d.viewedDate && (
                <Box className={classes.tlItem}>
                  <Box className={classes.tlDot} />
                  <Box>
                    <Text className={classes.tlTitle}>{t("invoice:detail.history.viewed")}</Text>
                    <Text className={classes.tlSub}>{dateTimeFormat(d.viewedDate)}</Text>
                  </Box>
                </Box>
              )}
              {d.sentDate && (
                <Box className={classes.tlItem}>
                  <Box className={classes.tlDot} />
                  <Box>
                    <Text className={classes.tlTitle}>{t("invoice:detail.history.sent")}</Text>
                    <Text className={classes.tlSub}>
                      {[d.senderUser ? renderName(d.senderUser) : null, dateTimeFormat(d.sentDate)]
                        .filter(Boolean)
                        .join(" · ")}
                    </Text>
                  </Box>
                </Box>
              )}
              {(d.createdAt || (!d.sentDate && !d.confirmedDate)) && (
                <Box className={classes.tlItem}>
                  <Box className={classes.tlDot} />
                  <Box>
                    <Text className={classes.tlTitle}>{t("invoice:detail.history.created")}</Text>
                    {d.createdAt && <Text className={classes.tlSub}>{dateTimeFormat(d.createdAt)}</Text>}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {params.id && params.id !== "new" ? (
            <Box className={classes.orderReturns}>
              <SafeRelatedReturns
                httpClient={httpRequest}
                target={{ kind: "invoice", invoiceId: params.id }}
                orderAdminBaseUrl={getModuleUrl("order")}
              />
            </Box>
          ) : null}
        </Box>
      </Box>

      {pdfOpen && pdfUrl && (
        <Box
          className={classes.ebarimtModalScrim}
          onClick={() => {
            setPdfOpen(false)
            setPdfUrl("")
          }}
        >
          <Box className={classes.ebarimtModalFrame} onClick={(e) => e.stopPropagation()}>
            <iframe src={pdfUrl} width="100%" height="100%" title="ebarimt" style={{ border: "none" }} />
          </Box>
        </Box>
      )}
    </Page>
  )
}
